/**
 * 🔒 Site types (piece 10, piece 15; decision 10100; T3476): the managed list of what kind of place a site is, seeded with
 * the owner's four. Settings › Organization › Site Types edits it; + Add Site and the Site's Edit form pick from its active
 * rows; the Sites list reads each site's type in its Type column and narrows by it from a row of segments. 🔨 Every path
 * is relative to the API root.
 */

/** The Settings page. */
export const SITE_TYPES_ROUTE = '/settings/organization/site-types';
/** The page's list and its add, behind the administrative capability as Departments is. */
export const SITE_TYPES_PATH = '/settings/site-types';
/** The active types the pickers and the list's segments read, under the Site module's read. */
export const SITE_TYPE_OPTIONS_PATH = '/pages/site-types';

/** One type's rename, its notes, its order, its retire and restore, and its delete. */
export function siteTypePath(siteTypeId: string): string {
  return `${SITE_TYPES_PATH}/${encodeURIComponent(siteTypeId)}`;
}

/** The address parameter the Sites list's segment rides in: the type's name, which the list's criteria match on. */
export const SITE_TYPE_PARAM = 'siteType';
/** The Sites list's Type column, the one a segment's criterion reads. */
export const SITE_TYPE_COLUMN = 'siteType';

/** The longest name and notes the window takes. */
export const SITE_TYPE_NAME_MAX = 80;
export const SITE_TYPE_NOTES_MAX = 200;

/** One type as Settings lists it, with how many sites carry it, so a delete is read against its use. */
export interface SiteTypeRow {
  id: string;
  version: number;
  name: string;
  sortOrder: number;
  isActive: boolean;
  notes: string | null;
  siteCount: number;
}

/** What the add window and a row's pencil send. */
export interface SiteTypeDraft {
  name: string;
  notes: string;
}

/** One active type as a picker offers it. */
export interface SiteTypeOption {
  value: string;
  label: string;
}

/** 🔒 What `GET pages/site-types` answers (decision 10500): the active types, and whether this viewer may add one, which shows the field's Add New Site Type row. */
export interface SiteTypeOptionsAnswer {
  rows: SiteTypeOption[];
  mayAdd: boolean;
}

/** A row's move in the order. */
export type SiteTypeMove = 'up' | 'down';

/** The refusal a draft meets before anything is sent, in the sentence the server answers with; null passes. */
export function siteTypeProblem(draft: SiteTypeDraft): string | null {
  const name = draft.name.trim();
  if (!name) return 'A site type has a name.';
  if (name.length > SITE_TYPE_NAME_MAX) return `A site type's name is at most ${SITE_TYPE_NAME_MAX} characters.`;
  if (draft.notes.trim().length > SITE_TYPE_NOTES_MAX) return `A site type's notes are at most ${SITE_TYPE_NOTES_MAX} characters.`;
  return null;
}

/** The key two names are compared on: the case and the outer spaces set aside, as the table's unique index folds them. */
export function siteTypeNameKey(name: string): string {
  return name.trim().toLowerCase();
}

/** 🔨 The refusal of a second type under a name already on the list. */
export function siteTypeTakenRefusal(name: string): string {
  return `${name.trim()} is already a site type.`;
}

/** 🔒 The refusal of a delete a site stands in the way of, naming how many, and offering Retire instead (decision 10100). */
export function siteTypeInUseRefusal(name: string, count: number): string {
  return `${name} is the type of ${count} ${count === 1 ? 'site' : 'sites'}, so it cannot be deleted. Retire it instead.`;
}

/** 🔨 The refusal of a retired type chosen for a site, naming it. */
export function siteTypeRetiredRefusal(name: string): string {
  return `${name} is a retired site type. Restore it on Settings › Organization › Site Types, or choose another.`;
}
